import {TranslateService} from '@ngx-translate/core';
import {
  ComponentFactory,
  ComponentFactoryResolver,
  EventEmitter,
  Inject,
  Injectable,
  ViewContainerRef
} from '@angular/core';
import {NavigateManagerService} from './navigate-manager.service';
import {WizardStepComponent} from '../components/wizard-step/wizard-step.component';
import {ActivatedRoute} from '@angular/router';
import {StepConfig} from '../configuration/step-config';
import {CalculationService} from './impl/calculation.service';
import {EventBusService} from './impl/event-bus.service';
import {isNullOrUndefined} from 'util';
import {NavigateApi} from './api/navigate.api';
import {SessionService} from './impl/session/session.service';
import {ConfigurationService} from './impl/configuration/configuration.service';
import {MetricsService} from '../../shared/services/metrics.service';
import {MetricsDataModel} from '../../shared/components/models/metrics/metrics-data.model';
import {CustomMetricsName} from '../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../shared/components/models/metrics/metrics-type.enum';
import {OfferService} from './impl/offer.service';
import {StepKey} from '../enums/step-key.enum';
import {InsuranceCompaniesAdditionalValidationService} from './insurance-companies-additional-validation.service';
import {RegistrationService} from './impl/registration.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {ApplicationStateService} from '../../shared/services/application-state.service';
import {AppMode} from '../../shared/models/app-mode.enum';
import {UrlParamsService} from './impl/url-params.service';
import {AztecScannerService} from '../../shared/services/aztec-scanner.service';
import {SectionModeState, SectionsStateService} from '../../shared/services/sections-state.service';
import {VehicleModelStateService} from './impl/section-services/vehicle-model/vehicle-model-state.service';
import {BusinessParamName} from '../../shared/models/request-offer/request-offer.enum';
import {ObjectUtils} from '../utils/object.utils';
import {SalesChannelAnalyticsService} from '../../shared/services/sales-channel-analytics.service';
import {CalculationCacheService} from './impl/calculation-cache.service';
import {OfferSummaryState} from '../../shared/components/offer-summary/offer-summary.component';
import {ScrollHelper} from '../../shared/services/scroll-helper';
import {StepValidationStorageService} from '../../shared/services/step-validation-storage.service';
import {
  isCarOne,
  isEmbeddedInIframe,
  isKIOSKPOLIS,
  isMobile,
  isNestBank,
  isNext,
  isSkycash,
  visibleInCustomWidth
} from '../utils/utils';
import {ElementVisibility} from './impl/configuration/internal-config';
import * as _ from 'lodash';
import {InternalConfigService} from '../../shared/services/internal-config.service';
import {ScrollService} from '../../shared/services/scroll.service';
import {NextBannersService} from '../../shared/services/next-banners.service';
import {OffersTableService} from './impl/offers-table.service';
import {Offer} from '../models/offer/offer.model';
import {PaymentMethod} from '../consts/payment-method.enum';
import {ApplicationContextService} from './impl/configuration/application-context.service';
import {NotificationPath, NotificationService} from './impl/notification.service';
import {NotificationStateService} from '../../shared/services/notification-state.service';
import {DeviceService} from '../../shared/services/device.service';
import {NextSuccessNotificationContext} from '../../shared/models/notification-state';
import {QueryParamName} from '../enums/query-param-name.enum';
import {ComponentsFactoryService} from '../../shared/services/components-factory.service';
import {AgreementsService} from '../../shared/services/agreements.service';
import {NativeIframeCommunicationService} from '../../shared/services/native-iframe-communication.service';

@Injectable({
  providedIn: 'root'
})
export class WizardService implements NavigateApi {

  stepChange$: EventEmitter<string> = new EventEmitter();
  aboveButtonSectionsContainer: ViewContainerRef;
  belowButtonSectionsContainer: ViewContainerRef;
  displayStepButton = false;
  public offerSummaryState: OfferSummaryState;
  stepBottomVisibilityEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  stepBottomVisible: boolean;
  isNext: boolean = isNext();
  isEmbeddedInIframe: boolean = isEmbeddedInIframe();

  private activeWizardStep: WizardStepComponent;
  private _componentFactory: ComponentFactory<WizardStepComponent>;
  private _nextButtonCaption: string;
  private _nextButtonDisabled: boolean = false;

  private stepsMetrics: MetricsDataModel[];

  constructor(@Inject(ComponentFactoryResolver) private factoryResolver: ComponentFactoryResolver,
              private stepValidationStorageService: StepValidationStorageService,
              private navigateManager: NavigateManagerService,
              private offerService: OfferService,
              private sectionStateService: SectionsStateService,
              private calculationService: CalculationService,
              private eventBusService: EventBusService,
              private ts: TranslateService,
              private aztecScannerService: AztecScannerService,
              private vehicleModelStateService: VehicleModelStateService,
              private iCAdditionalValidationService: InsuranceCompaniesAdditionalValidationService,
              private sessionService: SessionService,
              private agreementsService: AgreementsService,
              private deviceService: DeviceService,
              private metricsService: MetricsService,
              private stateService: ApplicationStateService,
              private registrationService: RegistrationService,
              private urlParamsService: UrlParamsService,
              private salesChannelAnalyticsService: SalesChannelAnalyticsService,
              private configurationService: ConfigurationService,
              private internalConfigService: InternalConfigService,
              private scrollService: ScrollService,
              private offersTableService: OffersTableService,
              private nextBannersService: NextBannersService,
              private contextService: ApplicationContextService,
              private notificationService: NotificationService,
              private notificationStateService: NotificationStateService,
              private calculationCacheService: CalculationCacheService,
              private componentsFactoryService: ComponentsFactoryService,
              private nativeIframeCommunicationService: NativeIframeCommunicationService) {
    this.subscribeToEvents();
    this.navigateManager.navigate = this;
  }

  get wizardConfig(): any {
    return this.navigateManager.wizardConfig;
  }

  set nextButtonCaption(value: string) {
    this._nextButtonCaption = value;
  }

  get nextButtonCaption(): string {
    return this._nextButtonCaption;
  }

  get nextButtonDisabled(): boolean {
    return this._nextButtonDisabled;
  }

  get customerAgreementScanningMode(): boolean {
    return this.vehicleModelStateService.customerAgreementScanningMode;
  }

  get stepButtonVisible(): boolean {
    if (this.contextService.isDiagnosticStationContext && (this.vehicleModelStateService.scanClientAgreementButtonVisible || this.customerAgreementScanningMode)) {
      return false;
    }
    if (this.contextService.isDiagnosticStationContext && this.currentStepKey === StepKey.FIRST_STEP_0 && !this.aztecScannedFromQrCode) {
      return false;
    }
    return !this.isThirdStep || (this.isThirdStep && !visibleInCustomWidth(761)) || isSkycash();
  }

  get offerSummaryVisible(): boolean {
    const offersTableView = this.isOffersStep && this.offersTableService.isTableView && this.offersTableService.selectedOffer;
    return (this.isThirdStep || (isKIOSKPOLIS() && offersTableView)) && !isSkycash();
  }

  get offerSummaryOffer(): Offer | undefined {
    if (isKIOSKPOLIS() && this.isOffersStep && this.offersTableService.selectedOffer) {
      return this.offersTableService.selectedOffer;
    }
    return undefined;
  }

  get isValidStep(): boolean {
    if (this.activeWizardStep) {
      return this.activeWizardStep.isValidStep();
    }
    return false;
  }

  get isThirdStep(): boolean {
    return this.activeWizardStep ? this.activeWizardStep.stepKey === StepKey.THIRD_STEP : false;
  }

  get isOcDetailsStep(): boolean {
    return this.navigateManager.isOcDetailsStep;
  }

  get isVehicleModelStep(): boolean {
    return this.navigateManager.isVehicleModelStep;
  }

  get isOffersStep(): boolean {
    return this.navigateManager.isOffersStep;
  }

  get isOrderPolicyStep(): boolean {
    return this.navigateManager.isOrderPolicyStep;
  }

  get errorsOnStep(): boolean {
    if (!isNullOrUndefined(this.activeWizardStep)) {
      return this.activeWizardStep.errorsOnStep();
    }
  }

  get currentStep(): StepConfig {
    return this.navigateManager.currentStep;
  }

  get currentStepKey(): StepKey {
    return this.currentStep ? this.currentStep.stepKey : null;
  }

  get currentStepIndex(): number {
    return this.navigateManager.currentStepIndex;
  }

  get stepsConfig(): StepConfig[] {
    return this.navigateManager.stepsConfig;
  }

  get nextStepName(): string {
    return this.navigateManager.getNextStepName();
  }

  get shouldShowNextStepInfo(): boolean {
    return !isNullOrUndefined(this.nextStepName) && this.nextStepName.length > 0;
  }

  get stepsKeys(): string[] {
    return this.configurationService.uiConfig.navigateConfig.steps.map(step => step.stepKey);
  }

  get sectionsBelowStepButtonConfig(): any[] {
    if (this.currentStep.sectionsUnderStepButton) {
      return this.currentStep.sectionsUnderStepButton;
    }
    return [];
  }

  get distinctStepKeysConfigs(): StepConfig[] {
    return this.navigateManager.distinctStepKeysConfigs;
  }

  get stepsForNavigation(): StepConfig[] {
    return this.stepsConfig.filter(step => !isNotNullOrUndefined(step.skipInNavigation) || !step.skipInNavigation);
  }

  get skipToOffersActive(): boolean {
    const skipToStep = this.urlParamsService.getQueryParam(QueryParamName.SKIP_TO_STEP);
    return skipToStep ? atob(skipToStep) === 'offers' : false;
  }

  get canInitAtOffers(): boolean {
    return this.skipToOffersActive &&
      !!this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_ID) &&
      this.stateService.firstEntry &&
      this.calculationService.isStartInsuranceDateValid;
  }

  get isNotificationStep(): boolean {
    return this.navigateManager.isNotificationStep;
  }

  get navigationForbidden(): boolean {
    return this.isNotificationStep || this.stateService.orderPolicySubmitted;
  }

  get headerLogoRedirectDisabled(): boolean {
    if (this.urlParamsService.logoRedirectDisabled) {
      return true;
    }
    if (this.stateService.orderPolicySubmitted) {
      return !this.isNotificationStep;
    }
    return false;
  }

  get isCurrentStepValidRealTime(): boolean {
    return this.currentStepKey ? this.stepValidationStorageService.isStepValidRealTime(this.currentStepKey) : true;
  }

  get readonly(): boolean {
    return this.stateService.mode === AppMode.READONLY || this.stateService.mode === AppMode.NOTIFICATIONS || this.stateService.mode === AppMode.WAITING_FOR_OFFER_LOADING;
  }

  get isMobile(): boolean {
    return isMobile();
  }

  get canInitAsOwnerDataStep(): boolean {
    if (this.stateService.state.dataFromCalculationCache) {
      return (isKIOSKPOLIS() || isNestBank()) && this.calculationService.isStartInsuranceDateValid && this.stateService.dataLoadedFromCalculationsCache;
    }
    if (this.stateService.state.aztecScannedFromQrCode && !this.contextService.isDiagnosticStationContext) {
      return false;
    }
    if (isCarOne()) {
      return this.stateService.firstEntry && isNotNullOrUndefined(this.stateService.state.sessionCalculationData);
    }
    const agreements = this.calculationService.agreements;
    return this.isNext && this.stateService.firstEntry && agreements && agreements.length > 0;
  }

  get nextBannerVisible(): boolean {
    return this.nextBannersService.isBannerEnabledOnStep(this.currentStepKey);
  }

  get isDiagnosticStationContext(): boolean {
    return this.contextService.isDiagnosticStationContext;
  }

  get aztecScannedFromQrCode(): boolean {
    return this.stateService.state.aztecScannedFromQrCode;
  }

  init(aboveButtonSectionsContainer: ViewContainerRef, belowButtonSectionsContainer: ViewContainerRef, route: ActivatedRoute) {
    this.handleAztecCodeFromUrl();
    this.navigateManager.activatedRoute = route;
    this.navigateManager.init();
    this.aboveButtonSectionsContainer = aboveButtonSectionsContainer;
    this.belowButtonSectionsContainer = belowButtonSectionsContainer;
    this.initMetrics();
    this.calculationService.calculationDataReady.subscribe(data => {
      if (data) {
        if (!this.navigateManager.isNotificationStep) {
          if (this.canInitAtOffers) {
            this.initAsOffersStep();
          } else {
            if (!this.calculationService.isStartInsuranceDateValid) {
              this.calculationService.calculationData.insuranceData.startInsuranceDate = null;
            }
            if (this.canInitAsOwnerDataStep) {
              this.initAsOwnerDataStep(false);
            } else {
              this.createFirstStep();
            }
          }
        } else if (this.navigateManager.isNotificationStep) {
          this.navigateManager.currentStep = null;
        }
      }
    });
    if (this.isOffersStep && !this.stateService.firstEntry) { // refresh on offers step
      this.navigateManager.updateCacheOffer(true);
      if (this.navigateManager.isWaitingForOfferMode) {
        this.navigateManager.setAppMode(AppMode.RAW);
      }
      this.offerService.sendCalculation()
        .then(() => {
          this.offerService.noOffersEvent.take(1).subscribe(() => this.navigateManager.openNoOffersFoundDialog());
        })
        .catch(error => {
          console.error(error);
        });
    }
    this.navigateManager.openInitialDialogs();
    this.handleStartInsuranceDateChange();
    this.handleCustomerAgreementPhotoInit();
    this.agreementsService.init();
  }

  handleStartInsuranceDateChange(): void {
    if (isKIOSKPOLIS()) {
      this.calculationService.startInsuranceDateChange$.subscribe(() => {
        setTimeout(() => {
          if (this.isValidStep && this.currentStepKey === StepKey.FIRST_STEP_1) {
            if (!this.contextService.isDiagnosticStationContext) {
              this.initAsVehicleDataStep();
            }
          }
        }, 500);
      });
    }
  }

  handleCustomerAgreementPhotoInit(): void {
    if (isKIOSKPOLIS()) {
      this.calculationService.customerAgreementPhotoInit$.subscribe(() => {
        setTimeout(() => {
          if (this.isValidStep && this.currentStepKey === StepKey.FIRST_STEP_0) {
            if (this.contextService.isDiagnosticStationContext) {
              this.initAsOwnerDataStep(true);
            }
          }
        }, 500);
      });
    }
  }

  goNext(disconnectWs: boolean = true): Promise<boolean> | boolean {
    this.activeWizardStep.submit();
    if (!this.calculationService.validationEnabled || this.activeWizardStep.isValidStep()) {
      const isStepValid = this.navigateManager.performAdditionalStepValidation();
      if (isStepValid) {
        this.activeWizardStep.updatePreviewSectionState();
        let stepConfig;
        this.salesChannelAnalyticsService.sendDataForCurrentStep(this.currentStepIndex);

        if (this.navigateManager.isNextStepOffers) { // wait for postCalculation result
          this.navigateManager.updateCacheOffer(false);
          this.calculationCacheService.createCacheCalculation();
          this.metricsService.sendSimpleMetric('nextStepButton:disable', MetricsType.PROCESS);
          this._nextButtonDisabled = true;
          return this.offerService.sendCalculation()
            .then(() => {
              this.metricsService.sendSimpleMetric('nextStepButton:sendCalculationResolvedWithSuccess', MetricsType.PROCESS);
              if (!this.contextService.isDiagnosticStationContext) {
                this.metricsService.sendSimpleMetric('nextStepButton:enable:nonDiagnosticStationContext', MetricsType.PROCESS);
                this._nextButtonDisabled = false;
                if (!this.navigateManager.isWaitingForOfferMode) {
                  stepConfig = this.navigateManager.getNext();
                  this.offerService.noOffersEvent.take(1).subscribe(() => this.navigateManager.openNoOffersFoundDialog());
                  if (!isNullOrUndefined(stepConfig)) {
                    this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
                    this.createStep(stepConfig);
                    return true;
                  }
                } else { // waiting for offer mode
                  return this.offerService.offerFound.then(offerFound => {
                    if (offerFound) { // offer was matched
                      this.disconnectWebSocket();
                      if (isNotNullOrUndefined(this.registrationService.registrationState)) { // include registration data from session
                        this.registrationService.includeDataFromSession();
                      }
                      return this.goToStepInWaitingForOfferMode(this.navigateManager.afterOffersStepKey);
                    } else { // no offer was matched
                      if (this.offerService.offers.length > 0) {
                        return this.goToStepInWaitingForOfferMode(StepKey.SECOND_STEP);
                      } else {
                        this.navigateManager.openNoOffersFoundDialog();
                        return false;
                      }
                    }
                  })
                }
              } else {
                this.redirectToContactForwardedNotification();
                return true;
              }
            })
            .catch(error => { // FIXME handle failed postCalculation
              this.metricsService.sendSimpleMetric('nextStepButton:enable:sendCalculationError', MetricsType.PROCESS, JSON.stringify(error));
              this._nextButtonDisabled = false;
              return false;
            });
        } else {
          if (this.isThirdStep) {
            if (this.iCAdditionalValidationService.validate()) {
              return false;
            }
            if (this.registrationService.registration.owner.coOwners && this.registrationService.registration.owner.coOwners.length === 0) {
              delete this.registrationService.registration.owner.coOwners;
            }
          }

          if (this.isOffersStep) {
            this.offerService.resetDialogsState();
            if (disconnectWs === true) {
              this.disconnectWebSocket();
            }
          }

          if (this.isOcDetailsStep && isKIOSKPOLIS() && this.aztecScannedFromQrCode) {
            this.goToLastStepBeforeOffers();
            this.sectionStateService.setModeInAllSections(isMobile() ? SectionModeState.PREVIEW : SectionModeState.EDIT);
            return true;
          }

          stepConfig = this.navigateManager.getNext();
          if (!isNullOrUndefined(stepConfig)) {
            this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
            this.createStep(stepConfig);
            return true;
          }
          return false;
        }
      }
      return false;
    }
    return false;
  }

  goPrev() {
    let stepConfig = this.navigateManager.getPrev();
    if (!isNullOrUndefined(stepConfig)) {
      this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
      this.createStep(stepConfig);
    }
  }

  changeViewByStepConfig(stepConfig: StepConfig) {
    this.changeStepByStepKey(stepConfig.stepKey);
  }

  changeStepByStepKey(stepKey: string) {
    let availableFirstStep = this.navigateManager.getStepConfigWithValidationFromStart(stepKey);
    if (this.activeWizardStep) {
      this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
    }
    this.createStep(availableFirstStep);
  }

  moveToStep(stepKey: string) {
    this.changeStepByStepKey(stepKey);
  }

  browserNavigate(stepKey: StepKey) {
    this.changeStep(stepKey);
  }

  changeStep(stepKey: StepKey): Promise<void> | void {
    this.sessionService.canShowEntryInformationModal = false;
    // if (this.isThirdStep && stepKey === StepKey.SECOND_STEP) { // payment --> offers
    // if (this.navigateManager.isPrevStepOffers) { // payment --> offers
    if (this.navigateManager.isTargetStepOffers(stepKey)) { // payment --> offers
      this.navigateManager.updateAppMode();
      this.navigateManager.updateCacheOffer(true);
      return this.offerService.sendCalculation()
        .then(() => {
          this.offerService.noOffersEvent.take(1).subscribe(() => this.navigateManager.openNoOffersFoundDialog());
          this.navigateManager.moveToStep(stepKey);
        })
        .catch(error => { // FIXME handle failed postCalculation
          console.error(error);
        });
    } else {
      if (this.isOffersStep) { // leaving offers step
        this.offerService.resetDialogsState();
        this.disconnectWebSocket();
      }
      this.navigateManager.moveToStep(stepKey);
    }
  }

  clearStep() {
    this.aboveButtonSectionsContainer.clear();
  }

  handleScroll() {
    if (!this.readonly) {
      this.registerIntersectionObserver();
    } else {
      const subscription = this.stateService.appModeChange.subscribe(() => {
        if (!this.readonly) {
          this.registerIntersectionObserver();
          subscription.unsubscribe();
        }
      });
    }
  }

  resolveElementVisibility(elementConfig: any): boolean {
    if (!isNotNullOrUndefined(elementConfig)) {
      return false;
    }
    if (elementConfig.hasOwnProperty('visible')) {
      const origin = this.urlParamsService.getQueryParam(QueryParamName.ORIGIN);
      switch (elementConfig.visible) {
        case ElementVisibility.HIDDEN:
          return false;
        case ElementVisibility.ONLY_MOBILE:
          return this.isMobile && !(origin && origin.toUpperCase() === 'MOBILE');
        case ElementVisibility.ONLY_DESKTOP:
          return !this.isMobile;
        case ElementVisibility.VISIBLE:
          return true;
        case ElementVisibility.ORIGIN_WWW:
          return origin ? origin.toUpperCase() === 'WWW' : false;
        case ElementVisibility.ORIGIN_NOT_MOBILE:
          return origin ? origin.toUpperCase() !== 'MOBILE' : true;
      }
    } else {
      console.error('Visibility for given element is not defined', elementConfig);
      return false;
    }
  }

  isActiveOrPrevious(stepConfig: StepConfig): boolean {
    const stepIndex = _.findIndex(this.stepsForNavigation, step => step.additionalInfo.stepName === stepConfig.additionalInfo.stepName);
    const currentIndex = _.findIndex(this.stepsForNavigation, step => step.additionalInfo.stepName === this.currentStep.additionalInfo.stepName);
    return stepIndex <= currentIndex;
  }

  isActive(stepConfig: StepConfig): boolean {
    if (this.currentStep) {
      const stepIndex = _.findIndex(this.stepsForNavigation, step => step.additionalInfo.stepName === stepConfig.additionalInfo.stepName);
      const currentIndex = _.findIndex(this.stepsForNavigation, step => step.additionalInfo.stepName === this.currentStep.additionalInfo.stepName);
      return stepIndex === currentIndex;
    }
    return false;
  }

  shouldAllowToNavigate(stepConfig: StepConfig): boolean {
    if (this.navigationForbidden) {
      return false;
    }
    if (this.currentStep) {
      const clickedIndex = _.findIndex(this.distinctStepKeysConfigs, step => step.stepKey === stepConfig.stepKey);
      const currentIndex = _.findIndex(this.distinctStepKeysConfigs, step => step.stepKey === this.currentStep.stepKey);
      return (clickedIndex < currentIndex) && stepConfig.additionalInfo.stepName !== this.currentStep.additionalInfo.stepName;
    }
    return false;
  }

  navigateToClickedStep(clickedStep: StepConfig) {
    if (this.shouldAllowToNavigate(clickedStep)) {
      this.changeStep(clickedStep.stepKey);
    }
  }

  confirmOfferSelectedFromTable(): void {
    this.offersTableService.confirmSelectedOffer();
  }

  scanAztecCode(): void {
    if (this.deviceService.isMobileDevice) {
      this.aztecScannerService.openAztecScanner();
    } else {
      this.aztecScannerService.openAztecScannerQRDialog();
    }
  }

  private redirectToContactForwardedNotification(): void {
    this.clearStep();
    this.navigateManager.clearCurrentStep();
    this.notificationService.redirectToNotification(NotificationPath.SUCCESS);
    this.notificationStateService.setAppMode(AppMode.READONLY);
    this.notificationStateService.setNextSuccessNotification(NextSuccessNotificationContext.DIAGNOSTIC_STATION);
  }

  private registerIntersectionObserver(): void {
    const scrollHelper = new ScrollHelper();
    scrollHelper.registerElementVisibility('.wizard__bottom', this.stepBottomVisibilityEvent);
    this.stepBottomVisibilityEvent.subscribe(visible => {
      this.stepBottomVisible = visible;
    });
  }

  private initAsOwnerDataStep(scrollToFirstSection: boolean): void {
    this.navigateManager.markStepAndPreviousStepsAsValid(StepKey.FIRST_STEP_2);
    const stepConfig = this.navigateManager.getStepConfig(StepKey.FIRST_STEP_3);
    if (isNotNullOrUndefined(stepConfig)) {
      this.createStep(stepConfig, scrollToFirstSection);
    }
  }

  private initAsVehicleDataStep(): void {
    this.navigateManager.markStepAndPreviousStepsAsValid(StepKey.FIRST_STEP_1);
    const stepConfig = this.navigateManager.getStepConfig(StepKey.FIRST_STEP_2);
    if (isNotNullOrUndefined(stepConfig)) {
      this.createStep(stepConfig);
    }
  }

  private initAsOffersStep(): void {
    this.navigateManager.markStepAndPreviousStepsAsValid(StepKey.FIRST_STEP_3);
    const stepConfig = this.navigateManager.getStepConfig(this.navigateManager.offersStepKey);
    if (!isNullOrUndefined(stepConfig)) {
      this.createStep(stepConfig);
    }
    this.navigateManager.updateCacheOffer(false);
    this.offerService.sendCalculation()
      .then(() => {
        this.offerService.noOffersEvent.take(1).subscribe(() => this.navigateManager.openNoOffersFoundDialog());
        if (this.navigateManager.isWaitingForOfferMode) {
          this.offerService.offerFound.then(offerFound => {
            if (offerFound) { // offer was matched
              this.disconnectWebSocket();
              if (isNotNullOrUndefined(this.registrationService.registrationState)) { // include registration data from session
                this.registrationService.includeDataFromSession();
              }
              const canGoToPaymentStep = this.goToStepInWaitingForOfferMode(this.navigateManager.afterOffersStepKey);
              if (canGoToPaymentStep) {
                this.navigateManager.moveToStep(this.navigateManager.afterOffersStepKey);
              }
            } else { // no offer was matched
              if (this.offerService.offers.length === 0) {
                this.navigateManager.openNoOffersFoundDialog();
              }
              this.stateService.mode = AppMode.RAW;
            }
          })
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  private handleGeneraliInsuranceException(): boolean {
    return false;
  }

  private goToLastStepBeforeOffers() {
    this.navigateManager.markStepAndPreviousStepsAsValid(StepKey.FIRST_STEP_2);
    const stepConfig = this.navigateManager.getStepConfig(StepKey.FIRST_STEP_3);
    if (isNotNullOrUndefined(stepConfig)) {
      this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
      this.createStep(stepConfig);
    }
  }

  private goToStepInWaitingForOfferMode(stepKey: StepKey): boolean {
    const stepConfig = this.navigateManager.getStepConfig(stepKey);
    if (isNotNullOrUndefined(stepConfig)) {
      this.closeAndSendMetric(this.getStepMetric(this.activeWizardStep.stepKey));
      this.createStep(stepConfig);
      this.navigateManager.setAppMode(AppMode.WAITING_FOR_OFFER);
      if (stepKey === this.navigateManager.afterOffersStepKey) {
        this.offerService.resetDialogsState();
        this.navigateManager.markStepAsValid(this.navigateManager.offersStepKey);
      }
      return true;
    }
    return false;
  }

  private getTranslationKey(stepConfig: StepConfig): string {
    const additionalConfig = this.getAdditionalNavigateConfig();
    const contextStepConfig = this.contextService.getStepConfig(stepConfig.stepKey);
    if (contextStepConfig && contextStepConfig.buttonLabel) {
      return contextStepConfig.buttonLabel;
    }
    if (additionalConfig) {
      return additionalConfig.stepButtonLabel;
    } else if (stepConfig.hasOwnProperty('buttonLabel')) {
      return stepConfig.buttonLabel;
    } else {
      return stepConfig.stepKey !== StepKey.THIRD_STEP ? 'showOffers' : 'pay';
    }
  }

  private shouldClearContainers(stepConfig: StepConfig): boolean {
    return true;
    // return !isKIOSKPOLIS() || (!stepConfig.isOcDetailsStep && !this.isOcDetailsStep);
  }

  private isStepWithAccumulatedSections(stepConfig: StepConfig): boolean {
    if (isNotNullOrUndefined(this.internalConfigService.accumulatedSectionsSteps) && this.internalConfigService.accumulatedSectionsSteps.length > 0) {
      // all steps in this list has accumulated sections except the first one
      return this.internalConfigService.accumulatedSectionsSteps.findIndex(stepKey => stepKey === stepConfig.stepKey) > 0;
    }
    return false;
  }

  private createStep(stepConfig: StepConfig, scrollToFirstSectionAllowed: boolean = true) {
    this.scrollService.updateScrollPositionBeforeStepChange();
    this.getStepMetric(stepConfig.stepKey).open();
    this.clearContainers(stepConfig);

    this.activeWizardStep = this.createWizardStepComponent(stepConfig);
    this.ts.get(this.getTranslationKey(stepConfig)).subscribe(value => this.nextButtonCaption = value);
    this.createSectionsBelowStepButton();
    this.stateService.updateNavigationState(stepConfig);
    if (this.isThirdStep || (isKIOSKPOLIS() && this.isOffersStep)) {
      this.updateOfferSummaryState();
      setTimeout(() => this.handleScroll(), 0);
    }
    if (scrollToFirstSectionAllowed) {
      this.activeWizardStep.handleScrollBehaviour(scrollToFirstSectionAllowed);
    }
    this.updateSeoContentVisibility(stepConfig);
    this.stepChange$.emit(stepConfig.stepKey);
    this.nativeIframeCommunicationService.onStepCreated(this.isStepWithAccumulatedSections(stepConfig));
  }

  private createWizardStepComponent(stepConfig: StepConfig): WizardStepComponent {
    const componentFactory = this.factoryResolver.resolveComponentFactory(WizardStepComponent);
    const componentRef = this.aboveButtonSectionsContainer.createComponent(componentFactory);
    componentRef.instance.mobile = this.wizardConfig.mobile;
    componentRef.instance.stepConfig = stepConfig;
    componentRef.instance.sectionsConfig = this.getSectionsConfig(stepConfig);
    componentRef.instance.stepKey = stepConfig.stepKey;
    componentRef.instance.substepIndex = stepConfig.substepIndex;
    componentRef.instance.init();
    return componentRef.instance;
  }

  private getSectionsConfig(stepConfig: StepConfig): any[] {
    const stepsToAccumulate = this.getStepsToAccumulate(stepConfig);
    if (stepsToAccumulate && !!stepsToAccumulate.length) {
      const stepsToAccumulateSections = stepsToAccumulate.map(config => config.sections).flat();
      return [
        ...stepsToAccumulateSections,
        ...stepConfig.sections
      ]
    }
    return stepConfig.sections;
  }

  private getStepsToAccumulate(stepConfig: StepConfig): StepConfig[] {
    const accumulatedSectionsSteps = this.internalConfigService.accumulatedSectionsSteps;
    if (accumulatedSectionsSteps && !!accumulatedSectionsSteps.length) {
      const stepIndex = accumulatedSectionsSteps.findIndex(stepKey => stepKey === stepConfig.stepKey);
      if (stepIndex > -1) {
        return accumulatedSectionsSteps
            .slice(0, stepIndex)
            .map(stepKey => this.stepsConfig.find(config => config.stepKey === stepKey));
      }
    }
    return [];
  }

  private getStepSections(stepKey: string): any[] {
    return this.stepsConfig.find(config => config.stepKey === stepKey).sections;
  }

  private updateOfferSummaryState(): void {
    if (this.isThirdStep) {
      this.offerSummaryState = {
        buttonLabel: this.registrationService.paymentMethod === PaymentMethod.STANDARD_TRANSFER ? 'buy' : 'pay'
      }
    } else if (isKIOSKPOLIS() && this.isOffersStep) {
      this.offerSummaryState = {
        buttonLabel: 'OFFER_SECTION.SHARED.buyOnline'
      }
    }
  }

  private clearContainers(stepConfig: StepConfig) {
    if (this.shouldClearContainers(stepConfig)) {
      this.aboveButtonSectionsContainer.clear();
    }
    if (isNotNullOrUndefined(this.belowButtonSectionsContainer)) {
      this.belowButtonSectionsContainer.clear();
    }
  }

  private subscribeToEvents() {
    // update next button text
    this.eventBusService.changeButtonCaptionEvent.subscribe(event => {
      this.nextButtonCaption = event.caption;
      this.updateOfferSummaryState();
    });

    // clear active step (due to payment notification)
    this.eventBusService.clearActiveStepEvent.subscribe(() => {
      this.activeWizardStep = null;
      this.navigateManager.clearCurrentStep();
    });

    // update visibility of next step button
    this.eventBusService.nextStepButtonVisibilityEvent.subscribe(visibility => this.displayStepButton = visibility);
  }

  private createFirstStep() {
    let firstStep = this.navigateManager.getFirstStep();
    this.createStep(firstStep);
    if (isKIOSKPOLIS() && this.stateService.state.dataFromCalculationCache && !this.calculationService.isStartInsuranceDateValid) {
      this.scrollService.scrollToView('.vehicle-model-section__insurance-date', 300);
      const subscription = this.calculationService.startInsuranceDateChange$.subscribe(() => {
          this.initAsOwnerDataStep(true);
          if (!isMobile()) {
            this.sectionStateService.setModeInAllSections(SectionModeState.EDIT);
          }
          subscription.unsubscribe();
      });
    }
  }

  private initMetrics(): void {
    let stepsMetrics = [];
    let stepMetric: MetricsDataModel;

    this.stepsKeys.forEach(stepKey => {
      stepMetric = new MetricsDataModel(CustomMetricsName.VIEW_KEY, MetricsType.COMPONENT);
      stepMetric.value = stepKey;
      stepsMetrics.push(stepMetric);
    });
    this.stepsMetrics = stepsMetrics;
  }

  private closeAndSendMetric(metric: MetricsDataModel): void {
    metric.close();
    this.metricsService.send(metric);
  }

  private getStepMetric(stepKey: string): MetricsDataModel {
    return this.stepsMetrics.filter(metric => metric.value === stepKey)[0];
  }

  private disconnectWebSocket(): void {
    this.offerService.disconnectWS();
  }

  private createSectionsBelowStepButton(): void {
    const stepsToAccumulate = this.getStepsToAccumulate(this.currentStep);
    let sectionsBelowStepButton = this.sectionsBelowStepButtonConfig;
    if (stepsToAccumulate && !!stepsToAccumulate.length) {
      const stepsToAccumulateSections = stepsToAccumulate.map(config => config.sectionsUnderStepButton ? config.sectionsUnderStepButton : []).flat();
      sectionsBelowStepButton = [
        ...stepsToAccumulateSections,
        ...this.sectionsBelowStepButtonConfig
      ]
    }

    sectionsBelowStepButton.forEach(section => {
      const component = this.componentsFactoryService.getComponent(section.key);
      if (component) {
        const factory = this.factoryResolver.resolveComponentFactory(component);
        const componentRef = this.belowButtonSectionsContainer.createComponent(factory);
        if (section.hasOwnProperty('title')) {
          componentRef.instance['title'] = section.title;
        }
      } else {
        console.warn('No instance found for component key:', section.key);
      }
    });
  }

  private getAdditionalNavigateConfig(): any {
    if (this.configurationService.uiConfig.additionalNavigateConfig) {
      const config = this.configurationService.uiConfig.additionalNavigateConfig.steps.filter(stepConfig => stepConfig.stepKey === this.currentStep.stepKey);
      return config.length > 0 ? config[0] : null;
    }
    return null;
  }

  private handleAztecCodeFromUrl(): void {
    const encodedAztecCode = this.urlParamsService.getQueryParam(QueryParamName.AZTEC_ENCODED);
    if (isNotNullOrUndefined(encodedAztecCode)) {
      this.navigateManager.setAppMode(AppMode.NOTIFICATIONS);
      const decodedAztecCode = decodeURIComponent(encodedAztecCode);
      this.aztecScannerService.sendDecodedAztec(decodedAztecCode)
          .then(resp => {
            this.sectionStateService.prepareFirstStepAfterAztecData();
            this.stateService.handleRegistrationDataFromAztec(resp);
            this.handleCalculationDataFromAztec(resp);
            this.vehicleModelStateService.init();
          })
          .catch(err => {
            console.log(err)
          })
          .finally(() => {
            this.navigateManager.setAppMode(AppMode.RAW);
            this.urlParamsService.setQueryParam(QueryParamName.AZTEC_ENCODED, null);
          })
    }
  }

  private handleCalculationDataFromAztec(data: any): void {
    if (data.hasOwnProperty('calculationData')) {
      this.stateService.state.dataFromAztec = true;
      this.stateService.state.usedPreviewMode = true;
      const calculationData = this.mergeWithExistingCalculationData(data.calculationData);
      this.calculationService.dataFromSession.emit(calculationData);

      const sourceFromUrl = this.urlParamsService.getQueryParam(QueryParamName.SOURCE);
      if (!isNotNullOrUndefined(sourceFromUrl)) {
        this.stateService.saveBusinessParameter(BusinessParamName.CLIENT_SOURCE, 'aztec');
      }
    }
  }

  private mergeWithExistingCalculationData(calculationData: any): any {
    return ObjectUtils.mergeDeep(this.calculationService.calculationData, calculationData);
  }

  private updateSeoContentVisibility(stepConfig: StepConfig): void {
    const seoContentVisibilityCssClass = 'body--seo-content-active';
    const body = document.querySelector('body');
    if (this.configurationService.shouldDisplaySeoContent(stepConfig)) {
      if (!body.classList.contains(seoContentVisibilityCssClass)) {
        body.classList.add(seoContentVisibilityCssClass);
      }
    } else {
      body.classList.remove(seoContentVisibilityCssClass);
    }
  }
}
